import { computed, ref, Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import {
  lsGet,
  LsGlobalKeys,
  lsRemove,
  lsSave,
} from '@shared/utils/helpers/local-storage'
import getLocalizedLabel from '@shared/utils/helpers/localized-label'

import { ApiErrorCode } from '@protots/api_error_code'
import { ChatWidgetMessageType } from '@protots/chat_widget_message_type'

import { ChatWidget } from '@protos/apps/pc/chat_widget_pb'

import { CustomRoute } from '@pc/models/common/enums/custom-route'
import { IAuth, IRequestAuth } from '@pc/models/proto/dto/leads'
import { PatientSessionRO } from '@pc/models/proto/ro/leads'
import RouteNames from '@pc/router/route-names'
import { useDemoModeStore } from '@pc/store/demo'
import { useSessionStore } from '@pc/store/session'

import type {
  LocationQuery,
  NavigationFailure,
  NavigationGuard,
  RouteLocationNormalized,
  RouteLocationRaw,
} from 'vue-router'

class MissingDataError extends Error {
  key: string

  constructor(key: string) {
    super('MissingData!')
    this.key = key
  }
}

// Methods for initializing Patient Chat page; Socket, Teaser Message
// Also has idempotent methods for ensuring the init process has happened
// These methods should not require component setup context
export const useChatSetup = (userLocale: Ref<string>) => {
  const demoModeStore = useDemoModeStore()
  const sessionStore = useSessionStore()

  // Widget exists and Teaser message is sent
  const isWidgetTouched = ref(false)

  const sendTeaserMessage = (userLocale: Ref<string>, widget?: ChatWidget) => {
    const message = widget?.messages.find(
      (item) => item.messageType === ChatWidgetMessageType.TEASER
    )

    const text = getLocalizedLabel(
      message?.localizedTexts ?? [],
      userLocale.value
    ).value

    window.parent.postMessage(
      {
        id: 'teaser',
        isDisplay: message?.isDisplay,
        text: text,
        style: `2px solid ${document.documentElement.style.getPropertyValue(
          '--branding-color'
        )}`,
      },
      '*'
    )
  }

  const ensureIsWidgetTouched = () => {
    if (isWidgetTouched.value) {
      return
    }

    const chatWidget = sessionStore.chatProps?.chatWidget

    if (!chatWidget) {
      throw new MissingDataError('chatWidget')
    }

    document.documentElement.style.setProperty(
      '--branding-color',
      `${chatWidget?.branding?.data || ''}`
    )

    demoModeStore.updateDemoChatButton(chatWidget as ChatWidget)

    sendTeaserMessage(userLocale, chatWidget as ChatWidget)

    window.parent.postMessage('created', '*')

    isWidgetTouched.value = true
  }

  return {
    ensureIsWidgetTouched,
    sendTeaserMessage,
  }
}

// Utils for patient auth. Mostly getters.
// These methods should not require a component setup context
// Values from other composables in this file should NOT be used in this composable to avoid recursion
export const useAuthUtils = () => {
  const sessionStore = useSessionStore()
  const videoCallConfigsStore = useVideoCallConfigsStore()

  // Can unauthenticated patients skip auth and continue as visitor instead?
  const canSkipAuth = computed(() => {
    const chatWidget = sessionStore.chatProps?.chatWidget

    return chatWidget && !chatWidget.requireAuth
  })

  const continueAsVisitorLink = computed(
    () => sessionStore?.authRequest?.continueAsVisitorUrl
  )
  // Route to next step in Landing process. Either Landing Page or Conversation/ConversationsList
  const getLandingRoute = (query: LocationQuery): RouteLocationRaw => {
    const { chatProps } = sessionStore

    const goToGuestChat = chatProps?.visitant.case === 'videoCallGuest'

    if (goToGuestChat) {
      videoCallConfigsStore.roomId =
        chatProps?.visitant.case === 'videoCallGuest'
          ? chatProps.visitant.value.sessionId
          : ''

      return {
        name: RouteNames.guestChat,
        query,
      }
    }

    const goToNonQliqChat = chatProps?.visitant.case === 'qliqMessageUrl'

    if (goToNonQliqChat) {
      return {
        name: RouteNames.nonQliqChat,
        query,
      }
    }

    if (chatProps?.chatWidget && !chatProps?.chatWidget?.isAvailable) {
      return {
        name: RouteNames.widgetUnavailable,
        query,
      }
    }

    const isVisitor = chatProps?.visitant.case === 'visitor'
    const isKioskModeEnabled = chatProps?.chatWidget?.isKioskModeEnabled

    const isKioskModeInVisitor = isKioskModeEnabled && isVisitor

    const isLandingPageEnabled =
      isVisitor &&
      !chatProps.chatWidget?.isOneConversationPerPatientOrVisitor &&
      (chatProps?.chatWidget?.faqs?.length ||
        chatProps?.chatWidget?.branding?.splashScreenUrl)

    const goToLanding = isLandingPageEnabled || isKioskModeInVisitor

    if (goToLanding) {
      return {
        name: RouteNames.landingPage,
        query,
      }
    }

    const goToConversationsList =
      !chatProps?.chatWidget?.hideOldConversationForPatientVisitor &&
      !chatProps?.chatWidget?.isOneConversationPerPatientOrVisitor &&
      isVisitor &&
      chatProps.lastOpenConversationId

    // Route to conversation proper or conversation list depending on ChatWidget settings
    const conversationRouteName = goToConversationsList
      ? RouteNames.conversationsList
      : RouteNames.conversation

    // chatProps?.conversation?.id ||
    return {
      name: conversationRouteName,
      params: {
        conversationId: chatProps?.conversation?.id ?? CustomRoute.NULLABLE,
      },
      query,
    }
  }

  // Compute next route in Patient Auth process based on current Auth Status
  const getNextAuthRoute = (
    to: RouteLocationNormalized
  ): ReturnType<NavigationGuard> => {
    if (sessionStore.chatProps?.appId) {
      to.query.appId = sessionStore.chatProps?.appId
    }

    // if videoCallGuest got to GuestChat
    if (sessionStore.chatProps?.visitant.case === 'videoCallGuest') {
      videoCallConfigsStore.roomId =
        sessionStore.chatProps.visitant.value.sessionId
    }

    if (sessionStore.authSession) {
      // Important check.
      // If user is navigating to Routenames.home, they have just landed on the page and are waiting to be redirected
      // Home route has no content. User must be directed somewhere else.

      if (to.name === RouteNames.home) {
        return getLandingRoute(to.query)
      }

      return
    }

    if (sessionStore.authRequest) {
      return {
        name: RouteNames.authChallenge,
        query: to.query,
      }
    }

    if (sessionStore.authError === ApiErrorCode.PC_SHORT_URL_EXPIRED) {
      return {
        name: RouteNames.linkExpired,
        query: to.query,
      }
    }

    return {
      name: RouteNames.authFailed,
    }
  }

  // Checks routes that visitors are allowed to access without any authorization
  const isPublicRoute = (routeName: RouteNames) => {
    // * CAREFUL
    // Anything you add here will be visible and accessible by anybody regardless of authorization
    const publicRoutes = [
      RouteNames.authChallenge,
      RouteNames.authFailed,
      RouteNames.linkExpired,
      RouteNames.tac,
    ]

    return publicRoutes.includes(routeName)
  }

  return {
    canSkipAuth,
    continueAsVisitorLink,
    getLandingRoute,
    getNextAuthRoute,
    isPublicRoute,
  }
}

// Helpers for Auth Session validation and requests
// Methods here use vue-router and may need to be called within component setup context
export const useAuthHelpers = () => {
  const sessionStore = useSessionStore()

  const getAndSetAuthSession = async (routeQuery: LocationQuery) => {
    // Production Flow

    if (window.landing) {
      const { data } = PatientSessionRO.fromJson(window.landing)
      return sessionStore.patchSession(data)
    }

    // Dev Flow
    const { hash } = routeQuery

    if (hash) {
      lsSave(LsGlobalKeys.Hash, hash)
    }

    const storedHash = lsGet(LsGlobalKeys.Hash)

    if (!storedHash) {
      return
    }

    try {
      const patientSession = await sessionStore.fetchToken(storedHash)
      sessionStore.patchSession(patientSession)
    } catch (error) {
      lsRemove(LsGlobalKeys.Hash)
      throw error
    }
  }

  const ensureAuthorization = async (route: RouteLocationNormalized) => {
    if (sessionStore.authSession) {
      return
    }

    await getAndSetAuthSession(route.query)
  }

  // For fetching current auth status.
  // Basically just a wrapper for sessionStore.requestAuth that fills out params
  const refreshAuthStatus = async (
    payload: Omit<IRequestAuth, 'leadingProps'>
  ) =>
    await sessionStore.requestAuth({
      leadProps: sessionStore.leadProps,
      ...payload,
    })

  // For submitting auth secret when using dob/otp authentication
  // Basically just a wrapper for sessionStore.postAuth that fills out params
  const submitSecret = async (payload: Omit<IAuth, 'leadingProps'>) =>
    await sessionStore.postAuth({
      leadProps: sessionStore.leadProps,
      ...payload,
    })

  return {
    ensureAuthorization,
    getAndSetAuthSession,
    refreshAuthStatus,
    submitSecret,
  }
}

// Methods for Auth based navigation
// These methods require component setup context
export const useAuthNavUtils = () => {
  const route = useRoute()
  const router = useRouter()
  const sessionStore = useSessionStore()

  const { getLandingRoute } = useAuthUtils()
  // Navigate to the Landing Route from getLandingRoute
  // Allows using router.replace instead of router.push
  const navigateToLanding = (
    replace?: boolean
  ): Promise<void | NavigationFailure | undefined> => {
    const navMethod = replace ? router.replace : router.push

    return navMethod(getLandingRoute(route.query))
  }
  const navigateToConversation = (): Promise<
    void | NavigationFailure | undefined
  > => {
    const { chatProps } = sessionStore

    return router.push({
      name: RouteNames.conversation,
      params: {
        conversationId: chatProps?.conversation?.id ?? CustomRoute.NULLABLE,
      },
      query: route.query,
    })
  }
  const navigateToRouteFromQuery = (): Promise<
    void | NavigationFailure | undefined
  > | void => {
    const { chatProps } = sessionStore

    if (route.query.to) {
      return router.push({
        name: route.query.to.toString(),
        params: {
          conversationId:
            route.query.conversationId?.toString() ??
            chatProps?.conversation?.id ??
            CustomRoute.NULLABLE,
        },
        query: route.query,
      })
    }
  }

  return {
    navigateToRouteFromQuery,
    navigateToLanding,
    navigateToConversation,
  }
}

// Methods for using as or in route guards
// These methods should not require component setup context
export const useAuthGuards = () => {
  const { getNextAuthRoute, isPublicRoute } = useAuthUtils()
  const { ensureAuthorization } = useAuthHelpers()

  // Global router beforeEach
  const authBeforeEach = async (
    to: RouteLocationNormalized
  ): Promise<ReturnType<NavigationGuard>> => {
    try {
      // First, make sure we are properly authorized and have our session data in state
      await ensureAuthorization(to)
      // If the route is public, just navigate there
      if (isPublicRoute(String(to.name) as RouteNames)) {
        return
      }

      // Otherwise, figure out where to go based on current route and current auth status
      return getNextAuthRoute(to)
    } catch (e) {
      return {
        name: RouteNames.authFailed,
        query: to.query,
      }
    }
  }

  return { authBeforeEach }
}
